/**
 * A CARD LOOKUP'S PICKER FILTER (docs/plans/data-types-second-pass.md §3.7,
 * PR 6; the owner's ruling D10: "narrow, never refuse", plus `strict`).
 *
 * `options.filter` is stored KQL over the TARGET card's items. The picker
 * fetches with it ANDed into `?q=` and offers those items first; without
 * `strict` a plain **Show all** opens the whole card, because a person must
 * never be told an item does not exist because a filter hid it (a historical
 * work order on a vehicle that has since retired). With `options.strict` the
 * API refuses a NEW value outside the filter on every write path
 * (`lookup_filtered_out`, keepr-api utils/lookupFilter.js), so the form never
 * offers one: no Show all, and a quick create, a scan or "Me" that lands
 * outside it is said so rather than chosen.
 *
 * The API owns the rule; this module only reads the options and builds the
 * queries the picker sends.
 */
import { normalizeId } from './ids';

/** The element's filter text, trimmed, or '' when it has none. */
export const lookupFilterOf = options => (typeof options?.filter === 'string' ? options.filter.trim() : '');

/** Does this lookup refuse a value outside its filter? Only beside a filter. */
export const isStrictLookup = options => options?.strict === true && !!lookupFilterOf(options);

/** KQL string literal — the quote and the backslash are the only escapes. */
const literal = value => `"${String(value == null ? '' : value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

/**
 * `filter` AND `query`, each parenthesised so neither's `or` can reach into
 * the other. Either may be blank.
 */
export function andFilter(filter, query) {
	const a = String(filter || '').trim();
	const b = String(query || '').trim();
	if (!a) return b;
	if (!b) return a;
	return `(${a}) and (${b})`;
}

/** The query that asks whether item `id` is one the filter offers. */
export const offeredQuery = (filter, id) => andFilter(filter, `id = ${literal(normalizeId(id))}`);

/**
 * The target card and every card under it, by id — what the lookup accepts
 * (an item of a child card IS an item of the target), and so the cards a
 * filter over its items may speak about.
 */
export function lookupTargetCardIds(cardsById, targetId) {
	const target = normalizeId(targetId);
	const out = new Set();
	if (!target) return out;
	out.add(target);
	const cards = Object.values(cardsById || {});
	const descends = card => {
		const seen = new Set();
		let parent = normalizeId(card?.parentCardId);
		while (parent && !seen.has(parent)) {
			if (parent === target) return true;
			seen.add(parent);
			parent = normalizeId(cardsById?.[parent]?.parentCardId);
		}
		return false;
	};
	cards.forEach(card => {
		const id = normalizeId(card?._id);
		if (id && descends(card)) out.add(id);
	});
	return out;
}

/**
 * The field's sentence for a 400 `lookup_filtered_out { element, itemIds }`:
 * the record is outside the element's strict filter. Never "does not exist".
 */
export function describeFilteredOut(data, { elements = [] } = {}) {
	const def = (elements || []).find(entry => entry?.name && entry.name === data?.element);
	const label = def?.label?.singular || data?.element || 'This field';
	const several = Array.isArray(data?.itemIds) && data.itemIds.length > 1;
	return several
		? `${label} only takes items its filter offers, and some of these aren’t among them. Choose others.`
		: `${label} only takes items its filter offers, and this one isn’t among them. Choose another.`;
}

/**
 * The collection whose items the picker lists: the target's own when another
 * collection owns it (a global collection's, a linked one's, a parent's),
 * else the form's — keepr-api lookupFilter.homeCollectionId, so the editor's
 * builder and the picker ask the collection the API compiles against.
 */
export function lookupSourceCollectionId(targetDef, collectionId) {
	const owner = targetDef?.scope === 'collection' ? normalizeId(targetDef.collection_id) : '';
	const own = normalizeId(collectionId);
	return owner && owner !== own ? owner : own;
}
